/**
 * OnlineStateMapper — 伺服器狀態 → 本地 GameState 映射
 *
 * 將伺服器的 PlayerView（snake_case JSON）轉換為前端的 GameState 結構。
 * 當本地玩家是 server player2 時，自動交換 player1↔player2 視角，
 * 使 gameState.player1 永遠代表「自己」（畫面底部）。
 */

import { loadAllCards } from './CardLoader';
import type {
  GameState,
  Player,
  Card,
  Battlefield,
  BattleSide,
  BattlefieldSlot,
  BattlePosition,
  TurnPhase,
  GameStatus,
  CardLocation,
  CardOrientation,
} from '../types';

// ============================================================
// Server 類型定義（對應 Go JSON）
// ============================================================

interface ServerCardInstance {
  instance_id: string;
  definition_id: string;
  owner_id: string;
  location: string;
  position?: {
    side: string;
    row: string;
    column: number;
  };
  orientation: string;
  atk_modifier: number;
  score_modifier: number;
  summoned_this_turn: boolean;
}

interface ServerPlayerState {
  id: string;
  name: string;
  faction: string;
  deck: ServerCardInstance[];
  hand: ServerCardInstance[];
  graveyard: ServerCardInstance[];
  forest: ServerCardInstance[];
}

/** Server PendingChoice structure */
interface ServerPendingChoice {
  id: string;
  player_id: string;
  type: string;
  source_card_id: string;
  effect_text: string;
  filter?: {
    faction?: string;
    name?: string;
    row?: string;
    min_atk?: number;
    max_atk?: number;
  };
  filter_side?: string;
  filter_zone?: string;
  min_count: number;
  max_count: number;
  options?: { id: string; label: string }[];
  optional?: boolean;
  timeout_ms?: number;
}

interface ServerPlayerView {
  player1: ServerPlayerState;
  player2: ServerPlayerState;
  battlefield: (ServerCardInstance | null)[][][]; // [2 sides][2 rows][5 cols]
  turn_number: number;
  active_player_id: string;
  current_phase: string;
  countdown_turns: number | null;
  status: string;
  your_side: string;
  // Choice system
  pending_choice?: ServerPendingChoice;
  // Timer fields
  game_started_at?: string;
  game_time_limit?: number;
  deck_build_time_limit?: number;
  timer_expired?: boolean;
  competition_mode?: boolean;
}

// ============================================================
// 伺服器 code → 前端 id 映射
// ============================================================

/**
 * 建立伺服器卡牌 code → 前端 id 的查找表。
 * Server codes: "EVL-0011" (uppercase, with dash)
 * Frontend codes: "EVL0011" (uppercase, no dash)
 * Frontend ids: "evl_evl0011" (lowercase, prefixed)
 *
 * 我們用正規化的 code（移除 dash、大寫）做 key 來匹配。
 */
let serverCodeToFrontendId: Map<string, string> | null = null;

function normalizeCode(code: string): string {
  return code.replace(/-/g, '').toUpperCase();
}

function getCodeMap(): Map<string, string> {
  if (serverCodeToFrontendId) return serverCodeToFrontendId;
  serverCodeToFrontendId = new Map();
  const allCards = loadAllCards();
  for (const [id, def] of allCards.entries()) {
    if (def.code) {
      serverCodeToFrontendId.set(normalizeCode(def.code), id);
    }
    // Also map the id itself
    serverCodeToFrontendId.set(normalizeCode(id), id);
  }
  return serverCodeToFrontendId;
}

/**
 * 將伺服器的 definition_id 轉為前端 id。
 * 嘗試順序: 直接匹配 → 正規化匹配 → 原值返回
 */
function resolveDefinitionId(serverDefId: string): string {
  const map = getCodeMap();
  // Direct match
  if (map.has(serverDefId)) return serverDefId;
  // Normalized match
  const normalized = normalizeCode(serverDefId);
  const frontendId = map.get(normalized);
  if (frontendId) return frontendId;
  // Fallback: return as-is
  return serverDefId;
}

// ============================================================
// 主映射函數
// ============================================================

/** Mapped pending choice for frontend use */
export interface MappedPendingChoice {
  id: string;
  type: 'target' | 'card' | 'option' | 'position' | 'number';
  sourceCardId: string;
  sourceCardName: string;
  effectText: string;
  playerId: string;
  filter?: {
    side?: 'self' | 'enemy' | 'both' | 'any';
    zone?: 'battlefield' | 'hand' | 'graveyard' | 'deck' | 'forest';
    faction?: string;
    row?: 'front' | 'back' | 'any';
  };
  minCount?: number;
  maxCount?: number;
  options?: { id: string; label: string }[];
  optional?: boolean;
  createdAt: number;
}

/**
 * Extract and map a pending choice from server state (if present).
 */
export function extractPendingChoice(rawView: unknown): MappedPendingChoice | null {
  const view = rawView as ServerPlayerView;
  if (!view.pending_choice) return null;
  const pc = view.pending_choice;
  return {
    id: pc.id,
    type: (pc.type || 'target') as MappedPendingChoice['type'],
    sourceCardId: pc.source_card_id || '',
    sourceCardName: '',
    effectText: pc.effect_text || '',
    playerId: pc.player_id || '',
    filter: {
      side: (pc.filter_side || 'both') as 'self' | 'enemy' | 'both',
      zone: (pc.filter_zone || 'battlefield') as 'battlefield' | 'hand' | 'graveyard' | 'deck' | 'forest',
      faction: pc.filter?.faction,
      row: pc.filter?.row as 'front' | 'back' | 'any' | undefined,
    },
    minCount: pc.min_count || 1,
    maxCount: pc.max_count || 1,
    options: pc.options,
    optional: pc.optional ?? false,
    createdAt: Date.now(),
  };
}

/**
 * 將伺服器的 PlayerView 轉換為本地 GameState。
 *
 * @param rawView - 伺服器回傳的 state_sync payload 中的 state 物件
 * @param onlinePlayerSide - 本地玩家在伺服器的 side（'player1' 或 'player2'）
 * @returns 映射後的 GameState，player1 永遠是本地玩家
 */
export function mapServerViewToGameState(
  rawView: unknown,
  onlinePlayerSide: 'player1' | 'player2'
): GameState {
  const view = rawView as ServerPlayerView;
  const shouldSwap = onlinePlayerSide === 'player2';

  // 根據視角決定哪邊是「自己」
  const myServerPlayer = shouldSwap ? view.player2 : view.player1;
  const opponentServerPlayer = shouldSwap ? view.player1 : view.player2;

  const player1 = mapPlayerState(myServerPlayer);
  const player2 = mapPlayerState(opponentServerPlayer);

  const battlefield = mapBattlefield(view.battlefield, shouldSwap);

  // 映射 activePlayerId：如果需要交換，把 server 的 player ID 映射到 local side
  // 注意：activePlayerId 使用的是 server 的 player.id（UUID），不是 'player1'/'player2'
  // 但在本地系統中，activePlayerId 使用 player1.id / player2.id
  // 所以我們用 mapped player 的 id
  let activePlayerId = view.active_player_id;
  if (activePlayerId === myServerPlayer.id) {
    activePlayerId = player1.id; // 自己的回合
  } else {
    activePlayerId = player2.id; // 對手的回合
  }

  return {
    player1,
    player2,
    battlefield,
    turnNumber: view.turn_number,
    activePlayerId,
    currentPhase: view.current_phase as TurnPhase,
    attackDeclarations: [], // 由伺服器管理，本地不追蹤
    countdownTurns: view.countdown_turns,
    status: view.status as GameStatus,
    gameEnded: view.status === 'ended',
    winnerId: null, // 由 game_end 事件設定
    actionLog: [],
    tacticsActionUsed: false, // 由伺服器管理
    mechaMergePhaseActive: false, // 由伺服器管理
    mechaMergeUsedThisTurn: false, // 由伺服器管理
    // Timer fields
    gameStartedAt: view.game_started_at,
    gameTimeLimit: view.game_time_limit,
    deckBuildTimeLimit: view.deck_build_time_limit,
    timerExpired: view.timer_expired,
    competitionMode: view.competition_mode,
  };
}

// ============================================================
// 玩家狀態映射
// ============================================================

function mapPlayerState(serverPlayer: ServerPlayerState): Player {
  return {
    id: serverPlayer.id,
    name: serverPlayer.name,
    faction: serverPlayer.faction,
    deck: serverPlayer.deck.map(mapCardInstance),
    hand: serverPlayer.hand.map((c, i) => mapCardInstance(c, i)),
    graveyard: serverPlayer.graveyard.map(mapCardInstance),
    forest: serverPlayer.forest.map(mapCardInstance),
  };
}

// ============================================================
// 卡牌映射
// ============================================================

function mapCardInstance(serverCard: ServerCardInstance, index?: number): Card {
  // 對手手牌是隱藏的（instance_id === 'hidden'）
  if (serverCard.instance_id === 'hidden') {
    return {
      id: `hidden-${String(index ?? 0)}`,
      definitionId: 'hidden',
      ownerId: serverCard.owner_id,
      location: serverCard.location as CardLocation,
      orientation: 'upright' as CardOrientation,
    };
  }

  const card: Card = {
    id: serverCard.instance_id,
    definitionId: resolveDefinitionId(serverCard.definition_id),
    ownerId: serverCard.owner_id,
    location: serverCard.location as CardLocation,
    orientation: (serverCard.orientation || 'upright') as CardOrientation,
  };

  if (serverCard.position) {
    card.position = {
      side: serverCard.position.side as 'player1' | 'player2',
      row: serverCard.position.row as 'front' | 'back',
      column: serverCard.position.column as 1 | 2 | 3 | 4 | 5,
    };
  }

  if (serverCard.atk_modifier !== 0) {
    card.atkModifier = serverCard.atk_modifier;
  }
  if (serverCard.score_modifier !== 0) {
    card.scoreModifier = serverCard.score_modifier;
  }
  if (serverCard.summoned_this_turn) {
    card.summonedThisTurn = true;
  }

  return card;
}

// ============================================================
// 戰場映射
// ============================================================

/**
 * 將伺服器的 3D 陣列戰場映射為本地 Battlefield 結構。
 *
 * Server: Battlefield[2][2][5]
 *   [0] = server player1 side, [1] = server player2 side
 *   [0] = front row, [1] = back row
 *   [0-4] = columns 1-5
 *
 * 當 shouldSwap=true（本地玩家是 server player2），
 * 交換 side index：本地 player1Side ← server [1], 本地 player2Side ← server [0]
 */
function mapBattlefield(
  serverBf: (ServerCardInstance | null)[][][],
  shouldSwap: boolean
): Battlefield {
  const mySideIdx = shouldSwap ? 1 : 0;
  const opponentSideIdx = shouldSwap ? 0 : 1;

  return {
    player1Side: mapBattleSide(serverBf, mySideIdx, 'player1'),
    player2Side: mapBattleSide(serverBf, opponentSideIdx, 'player2'),
  };
}

function mapBattleSide(
  serverBf: (ServerCardInstance | null)[][][],
  sideIdx: number,
  localSide: 'player1' | 'player2'
): BattleSide {
  return {
    frontLine: mapRow(serverBf[sideIdx][0], localSide, 'front'),
    backLine: mapRow(serverBf[sideIdx][1], localSide, 'back'),
  };
}

function mapRow(
  serverRow: (ServerCardInstance | null)[],
  side: 'player1' | 'player2',
  row: 'front' | 'back'
): BattlefieldSlot[] {
  return serverRow.map((serverCard, colIdx) => {
    const column = (colIdx + 1) as 1 | 2 | 3 | 4 | 5;
    const position: BattlePosition = { side, row, column };

    if (!serverCard) {
      return { position, unit: null };
    }

    const card = mapCardInstance(serverCard);
    // 覆寫 position 為 local 座標
    card.position = position;

    return { position, unit: card };
  });
}

// ============================================================
// 指令位置轉換
// ============================================================

/**
 * 將本地 BattlePosition 轉換為伺服器 BattlePosition。
 *
 * 在線上模式中，本地 player1Side 代表「自己」。
 * 如果本地玩家是 server player2，需要交換 side：
 *   local player1 → server player2
 *   local player2 → server player1
 */
export function localToServerPosition(
  localPos: BattlePosition,
  onlinePlayerSide: 'player1' | 'player2'
): { side: string; row: string; column: number } {
  if (onlinePlayerSide === 'player1') {
    // 不需交換
    return {
      side: localPos.side,
      row: localPos.row,
      column: localPos.column,
    };
  }

  // 需要交換 side
  return {
    side: localPos.side === 'player1' ? 'player2' : 'player1',
    row: localPos.row,
    column: localPos.column,
  };
}
